import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    DOCUMENT,
    Inject,
    OnDestroy,
    OnInit
} from '@angular/core';
import {FormControl, FormGroup} from '@angular/forms';
import {ActivatedRoute} from '@angular/router';
import {NgbDate} from '@ng-bootstrap/ng-bootstrap';
import {Actions} from '@ngrx/effects';
import {CartActions} from '@spartacus/cart/base/core';
import {RoutingService, TranslationService} from '@spartacus/core';
import {LaunchDialogService} from '@spartacus/storefront';
import {BehaviorSubject, Observable, Subscription} from 'rxjs';
import {distinctUntilChanged, filter, map} from 'rxjs/operators';

import {AimoBaseOrder, AimoBaseOrderList, AimoOrderEntry} from '../../model/cart.model';
import {AimoTemplateFacet} from '../../model/product.model';
import {AimoActiveCartService} from '../../service/cart/aimo-active-cart.service';
import {AimoGtmService} from '../../service/gtm/aimo-gtm.service';
import {AimoUserService} from '../../service/user/aimo-user.service';
import {AimoNgbDateParserFormatter} from '../shared/datepicker/aimo-datepicker-parser-formatter';
import {openCloseSpinner} from '../shared/utils/spinner/aimo-spinner-utils';

import {CREATE_BASE_ORDER} from './base-order-modal/aimo-base-order-layout.config';

@Component({
    selector: 'aimo-base-orders',
    templateUrl: './aimo-base-orders.component.html',
    changeDetection: ChangeDetectionStrategy.Default,
    standalone: false
})
export class AimoBaseOrdersComponent implements OnInit, OnDestroy {
    baseOrderList$ = new BehaviorSubject<AimoBaseOrderList>({carts: []});
    searchForm: FormGroup;
    subscriptions: Subscription = new Subscription();
    selectedFacet: AimoTemplateFacet = {
        selected: true,
        code: 'all-facets',
    };

    sortEntries = [{code: 'name'}, {code: 'creationtime'}];

    // eslint-disable-next-line
    breadcrumbs$: Observable<any[]>;

    protected baseOrderId$;

    constructor(
        protected launchDialogService: LaunchDialogService,
        protected userService: AimoUserService,
        protected activeCartService: AimoActiveCartService,
        protected ngbDateParserFormatter: AimoNgbDateParserFormatter,
        protected activatedRoute: ActivatedRoute,
        protected routingService: RoutingService,
        protected cdr: ChangeDetectorRef,
        private lTranslation: TranslationService,
        protected actions$: Actions,
        protected gtmService: AimoGtmService,
        @Inject(DOCUMENT) private document: Document,
    ) {
    }

    pageAllowed(): Observable<boolean> {
        return this.userService.get().pipe(
            filter((u) => u !== undefined),
            map((u) => u.unit.allowInvoices),
        );
    }

    ngOnInit(): void {
        // eslint-disable-next-line
        this.breadcrumbs$ = this.lTranslation.translate('aimo.baseOrders.title').pipe(
            filter(Boolean),
            map((translation) => [{label: translation, link: '/baseorders'}]),
        );

        this.baseOrderId$ = this.routingService.getRouterState().pipe(
            map((routingData) => routingData.state.params.orderId),
            distinctUntilChanged(),
        );
        const searchTerm = this.activatedRoute.snapshot.queryParams?.searchTerm;
        const sortField = this.activatedRoute.snapshot.queryParams?.sortField;
        this.searchForm = new FormGroup({
            search: new FormControl(searchTerm),
            sortField: new FormControl(sortField ?? this.sortEntries[0].code),
        });
        this.fetchBaseOrders();
        this.subscriptions.add(
            this.launchDialogService.dialogClose.subscribe((next) => {
                if (next && this.activatedRoute.snapshot.url.length == 1) {
                    this.fetchBaseOrders();
                }
            }),
        );
        this.subscriptions.add(
            this.actions$.subscribe((action) => {
                if (action.type === CartActions.DELETE_CART_SUCCESS || action.type === CartActions.LOAD_CART_SUCCESS) {
                    this.fetchBaseOrders();
                }
            }),
        );
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }

    openCreateBaseOrderModal(): void {
        this.launchDialogService.closeDialog(null);
        this.launchDialogService.openDialogAndSubscribe(CREATE_BASE_ORDER, undefined, {
            createNew: true,
        });
    }

    fetchBaseOrders(): void {
        this.activeCartService.getBaseOrders(
            this.searchForm.controls['search'].value, {
                field: this.searchForm.controls['sortField'].value,
                descending: this.searchForm.controls['sortField'].value === 'creationtime'
            }).forEach((next) => {
            this.baseOrderList$.next(next);
            openCloseSpinner(this.document, false);
        });
        this.cdr.detectChanges();
    }

    getDeliveryDatesAhead(baseorder: AimoBaseOrder): number {
        return baseorder?.deliveryDatesAhead;
    }

    convertDate(d: string): NgbDate {
        return d ? this.ngbDateParserFormatter.parse(new Date(d)) : null;
    }

    pushGtmEvent(event: string): void {
        this.gtmService.pushEvent(event);
    }

    protected getSampleEntries(baseOrder: AimoBaseOrder): AimoOrderEntry[] {
        return baseOrder.entries.slice(0, (baseOrder.entries?.length > 6 ? 5 : baseOrder.entries?.length));
    }
}
